import { useContext, useEffect, useState, Fragment, useMemo } from "react";
import { Button } from "react-bootstrap";
import { BsArrowLeft } from "react-icons/bs";
import { MdContentCopy } from "react-icons/md";

import NormalTable, { ITableRowData, IColumn } from "../../../shared/components/normal-table/normal-table";
import SimpleModal from "../../../shared/components/simple-modal/simple-modal";
import Spinner from "../../../shared/components/spinner/spinner";
import Tooltip from "../../../shared/components/tooltip/tooltip";
import { roleWrite, roleAdmin } from "../../../shared/constants/user-roles";
import AuthContext from "../../../shared/context/auth-context";
import {useHttpNonTable} from "../../../shared/hooks/use-httpNonTable";
import { getAllDataNonTable, postSingleNonTable, IListPageData } from "../../../shared/utils/api";
import { RenderFields } from "../../../shared/utils/render-fields";
import { useBack } from "../../../shared/utils/use-back";
import { validateLoadingStatusNew, createUUID } from "../../../shared/utils/utils";
import { datamodelTokens } from "../../constants/backend-constants";
import "./token-list.scss";

const columns: IColumn[] = [
  {
    key: "creation_time",
    displayName: "Creation date",
    dataType: {
      type: "dateTime",
    }
  },
  {
    key: "active",
    displayName: "Active",
    dataType: {
      type: "text",
    },
  },
];

const modalColumns: IColumn[] = [
  {
    key: "name",
    displayName: "Name",
    dataType: {
      type: "text",
    },
    controlled: true,
    errorMsg: "The name is already in use"
  }
];

const TokenList = () => {
  const [data, setData] = useState<IListPageData | undefined>(undefined);
  const authCtx = useContext(AuthContext);
  const { handleBack } = useBack();
  const [modalData, setModalData] = useState<ITableRowData>({id : "", name: "", token: ""});
  const [unblockInput, setUnblockInput] = useState(false);
  const [sortedBy, setSortedBy] = useState("?order_by=name desc");
  const [modal, setModal] = useState(false);
  const [validated, setValidated] = useState(false);

  const { sendRequest: getSortBy, status: getSortByStatus, data: getSortByData, error: errorGetSortByData } = useHttpNonTable(getAllDataNonTable, true, authCtx.token, "token");
  const { sendRequest: postNewToken, status: postNewTokenStatus, data: postNewTokenData, error: errorPostNewToken } = useHttpNonTable(postSingleNonTable, false, authCtx.token, "token");
  const { sendRequest: postDeActivate, status: postDeActivateStatus, error: errorPostDeActivate } = useHttpNonTable(postSingleNonTable, false, authCtx.token, "token");

  useEffect(() => {
    const rolesArray = authCtx.selectedTenant.roles;
    if (rolesArray.indexOf(roleWrite) !== -1 || rolesArray.indexOf(roleAdmin) !== -1) {
      setUnblockInput(true);
    }
  }, [authCtx.selectedTenant, authCtx.systemAdmin]);

  useEffect(() => {
    getSortBy("token_list", "?order_by=name desc");
  }, [getSortBy]);

  useEffect(() => {
    setValidated(false);
  }, [modalData.name]);

  useEffect(() => {
    if (getSortByStatus === "completed" && !errorGetSortByData) {
      setData({rows: getSortByData});
    }
  }, [getSortByStatus]);

  useEffect(() => {
    if (postDeActivateStatus === "completed" && !errorPostDeActivate) {
      getSortBy("token_list", `${sortedBy}`);
    }
  }, [postDeActivateStatus]);

  useEffect(() => {
    if (postNewTokenStatus === "completed" && !errorPostNewToken) {
      getSortBy("token_list", `${sortedBy}`);
      setModalData({...modalData, token: postNewTokenData.token});
    }
  }, [postNewTokenStatus]);

  const handleOnDelete = (ids: string[]) => {
    postDeActivate(`deactivate_token${sortedBy}`, { ids });
  };

  const sortBy = (sortingQuery: string) => {
    getSortBy("token_list", `${sortingQuery}`);
    setSortedBy(sortingQuery);
  };

  const matchUniqueValues = () => {
    let matchedVal = false;
    const uniqueKeys = ["name"];
    uniqueKeys?.forEach((key: string) => {
      const popupUniqueKeyData = modalData[key];
      data?.rows.forEach((row) => {
        if (row[key] === popupUniqueKeyData) {
          matchedVal = true;
          setModalData({ ...modalData, [key] : "" });
        }
      });
    });
    return matchedVal;
  };

  const handleOnGenerateToken = () => {
    if (matchUniqueValues()) {
      setValidated(true);
    } else {
      postNewToken("create_token", { id: createUUID(),  name: modalData.name});
    }
  };

  const handleOnChange = (targetVal: string, inputKey: string) => {
    setModalData((prevState) => ({ ...prevState, [inputKey]: targetVal }));
  };

  const handleModalClosed = () => {
    setModal(false);
    setModalData({...modalData, token: "", name: "", id: ""});
  };

  const memorizedPrint = useMemo(() => {
    let defineColumns = [...modalColumns];
    if (modalData.token) {
      defineColumns = [...defineColumns, {
        key: "token",
        displayName: "Token",
        dataType: {
          type: "textarea",
        }
      },];
    }
    return (
      <Fragment>
        <div>
          {modalData.token && <div className="popupMessage">
            <span className="importantLine">Important:</span>{"Make sure to save the new token, It won't be accessible after closing."}
          </div>}
          <div className="tokenList-popupInputsWrapper">
            <RenderFields 
              fields={defineColumns} data={modalData} 
              handleOnChange={handleOnChange}
            />
          </div>       
        </div>
      </Fragment>
    );
  }, [modalData]);

  return (
    <div className="tokenList">
      {validateLoadingStatusNew([getSortByStatus, postNewTokenStatus, postDeActivateStatus]) && <Spinner />}
      <SimpleModal
        extraButtons={modalData.token &&  <Tooltip content={"Copy the token"} direction="top" delay={350} ><Button onClick={() => navigator.clipboard.writeText(modalData.token)} className="popup-action"><MdContentCopy /></Button></Tooltip>}
        actionButtonDisabled={modal && modalData.token ? true : false} 
        modalBody={memorizedPrint} buttonText={"Generate"} 
        mode={"New token"} 
        handleModalClosed={handleModalClosed} 
        handleSubmit={handleOnGenerateToken} 
        show={modal}
        validatedByParent={validated}
      />

      <div onClick={() => handleBack()} className="datasetDetails-nav-btn">
        <BsArrowLeft />
      </div>

      <div className="datasetDetails-header">
        <h1>Tokens</h1>
      </div>

      <NormalTable
        columns={[...modalColumns, ...columns]}
        sortBy={sortBy}
        defaultSortCol={"name"}
        onRowClickAction={() => console.log("click")}
        onAddButtonClickAction={() => setModal(true)}
        unblockInput={unblockInput}
        handleOnDelete={handleOnDelete}
        fetchedData={data}
        hideButtons={["generate", "settings", "save"]}
        dataUrl={datamodelTokens}
        allStatusDelete={postDeActivateStatus}
        allStatus={getSortByStatus}
      />
    </div>
  );
};

export default TokenList;
